import { UiTitleTreatmentType } from 'data/generated/graphql';
import {
  AllessehContentQueryBody,
  AllessehQuerySortKeyTypes,
  AllessehQuerySortOrderType
} from 'hooks/useAllessehContentQuery';
import * as BUSINESS from './sections/business';
import * as CFO_JOURNAL from './sections/cfoJournal';
import * as CIO_JOURNAL from './sections/cio-journal';
import * as CMO_TODAY from './sections/cmo-today';
import * as ECONOMY from './sections/economy';
import * as FINANCE from './sections/finance';
import * as HEALTH from './sections/health';
import * as JOURNAL_REPORTS from './sections/journal-reports';
import * as LATEST from './sections/latest';
import * as LIFESTYLE from './sections/lifestyle';
import * as LOGISTICS_REPORT from './sections/logistics-report';
import * as LISTEN from './sections/mediaCenter/listen';
import * as WATCH from './sections/mediaCenter/watch';
import * as PERSONAL_FINANCE from './sections/personal-finance';
import * as POLITICS from './sections/politics';
import * as BANKRUPTCY from './sections/pro/bankruptcy';
import * as CENTRAL_BANKING from './sections/pro/centralBanking';
import * as CYBERSECURITY from './sections/pro/cybersecurity';
import * as PRO_PRIVATE_EQUITY from './sections/pro/privateEquity';
import * as PRO_SB from './sections/pro/sustainableBusiness';
import * as VENTURE_CAPITAL from './sections/pro/ventureCapital';
import * as REAL_ESTATE from './sections/real-estate';
import * as RCJ from './sections/riskComplianceJournal';
import * as SCIENCE from './sections/science';
import { BASE_QUERY, BASE_QUERY_PRO, COMMON_QUERY_RULES_CONFIG } from './sections/shared/configs';
import * as SPORTS from './sections/sports';
import * as STYLE from './sections/style';
import * as TECH from './sections/tech';
import * as FUTURE_OF_EVERYTHING from './sections/theFutureOfEverything';
import * as US_NEWS from './sections/us-news';
import * as USA250 from './sections/usa250';
import * as WORLD from './sections/world';
import { DEFAULT_QUERY } from '../default';

export type QueryOptionWsj = {
  query: AllessehContentQueryBody;
  label: string;
  type:
    | WORLD.SECTION_ENUM
    | POLITICS.SECTION_ENUM
    | LIFESTYLE.SECTION_ENUM
    | REAL_ESTATE.SECTION_ENUM
    | FINANCE.SECTION_ENUM
    | BUSINESS.SECTION_ENUM
    | ECONOMY.SECTION_ENUM
    | TECH.SECTION_ENUM
    | PERSONAL_FINANCE.SECTION_ENUM
    | HEALTH.SECTION_ENUM
    | STYLE.SECTION_ENUM
    | SPORTS.SECTION_ENUM
    | SCIENCE.SECTION_ENUM
    | FUTURE_OF_EVERYTHING.SECTION_ENUM
    | CENTRAL_BANKING.SECTION_ENUM
    | CYBERSECURITY.SECTION_ENUM
    | VENTURE_CAPITAL.SECTION_ENUM
    | BANKRUPTCY.SECTION_ENUM
    | LOGISTICS_REPORT.SECTION_ENUM
    | CIO_JOURNAL.SECTION_ENUM
    | CMO_TODAY.SECTION_ENUM
    | CFO_JOURNAL.SECTION_ENUM
    | RCJ.SECTION_ENUM
    | US_NEWS.SECTION_ENUM
    | WATCH.SECTION_ENUM
    | LISTEN.SECTION_ENUM
    | PRO_PRIVATE_EQUITY.SECTION_ENUM
    | null;
  url: string | null;
};

type QueryOptions = {
  [key: string]: QueryOptionWsj[];
};

type QueryConfig = {
  [key: string]: QueryOptions;
};

export const QUERY_CONFIG_WSJ: QueryConfig = {
  world: {
    SECTION_TOP: [{ query: WORLD.WORLD, label: 'World', type: null, url: null }],
    SECTION_TOP_FIVE_STORIES: [{ query: WORLD.WORLD, label: 'World', type: null, url: null }],
    LEADING_AND_SUPPORT: [
      { query: WORLD.AFRICA, label: 'Africa', url: 'world/africa', type: WORLD.SECTION_ENUM.AFRICA },
      {
        query: WORLD.AMERICAS,
        label: 'Americas',
        url: 'world/americas',
        type: WORLD.SECTION_ENUM.AMERICAS
      },
      { query: WORLD.ASIA, label: 'Asia', url: 'world/asia', type: WORLD.SECTION_ENUM.ASIA },
      { query: WORLD.CHINA, label: 'China', url: 'world/china', type: WORLD.SECTION_ENUM.CHINA },
      { query: WORLD.EUROPE, label: 'Europe', url: 'world/europe', type: WORLD.SECTION_ENUM.EUROPE },
      {
        query: WORLD.MIDDLE_EAST,
        label: 'Middle East',
        url: 'world/middle-east',
        type: WORLD.SECTION_ENUM.MIDDLE_EAST
      },
      { query: WORLD.INDIA, label: 'India', url: 'world/india', type: WORLD.SECTION_ENUM.INDIA },
      { query: WORLD.OCEANIA, label: 'Oceania', url: 'world/oceania', type: WORLD.SECTION_ENUM.OCEANIA },
      { query: WORLD.RUSSIA, label: 'Russia', url: 'world/russia', type: WORLD.SECTION_ENUM.RUSSIA },
      { query: WORLD.UK, label: 'U.K.', url: 'world/uk', type: WORLD.SECTION_ENUM.UK }
    ],
    MORE_LIST: [{ query: WORLD.WORLD, label: 'More in World', type: null, url: null }],
    VIDEO_LIST: [{ query: WORLD.VIDEO, label: 'Video', type: null, url: null }],
    RELATED_OPINION: [
      {
        query: WORLD.RELATED_OPINION,
        label: 'Related opinion',
        url: '',
        type: WORLD.SECTION_ENUM.RELATED_OPINION
      }
    ]
  },
  // a dirty way of adding configs for a new page instance
  world_test: {
    SECTION_TOP: [{ query: WORLD.WORLD, label: 'World', type: null, url: null }],
    SECTION_TOP_FIVE_STORIES: [{ query: WORLD.WORLD, label: 'World', type: null, url: null }],
    LEADING_AND_SUPPORT: [
      { query: WORLD.AFRICA, label: 'Africa', url: 'world/africa', type: WORLD.SECTION_ENUM.AFRICA },
      {
        query: WORLD.AMERICAS,
        label: 'Americas',
        url: 'world/americas',
        type: WORLD.SECTION_ENUM.AMERICAS
      },
      { query: WORLD.ASIA, label: 'Asia', url: 'world/asia', type: WORLD.SECTION_ENUM.ASIA },
      { query: WORLD.CHINA, label: 'China', url: 'world/china', type: WORLD.SECTION_ENUM.CHINA },
      { query: WORLD.EUROPE, label: 'Europe', url: 'world/europe', type: WORLD.SECTION_ENUM.EUROPE },
      {
        query: WORLD.MIDDLE_EAST,
        label: 'Middle East',
        url: 'world/middle-east',
        type: WORLD.SECTION_ENUM.MIDDLE_EAST
      },
      { query: WORLD.INDIA, label: 'India', url: 'world/india', type: WORLD.SECTION_ENUM.INDIA },
      { query: WORLD.OCEANIA, label: 'Oceania', url: 'world/oceania', type: WORLD.SECTION_ENUM.OCEANIA },
      { query: WORLD.RUSSIA, label: 'Russia', url: 'world/russia', type: WORLD.SECTION_ENUM.RUSSIA },
      { query: WORLD.UK, label: 'U.K.', url: 'world/uk', type: WORLD.SECTION_ENUM.UK }
    ],
    MORE_LIST: [{ query: WORLD.WORLD, label: 'More in World', type: null, url: null }],
    VIDEO_LIST: [{ query: WORLD.VIDEO, label: 'Video', type: null, url: null }],
    RELATED_OPINION: [
      {
        query: WORLD.RELATED_OPINION,
        label: 'Related opinion',
        url: '',
        type: WORLD.SECTION_ENUM.RELATED_OPINION
      }
    ]
  },
  // general purpose test page defaults to finance
  test: {
    SECTION_TOP: [{ query: CYBERSECURITY.SECTION_TOP, label: 'Cybersecurity - Top Story', type: null, url: null }],
    SECTION_TOP_FIVE_STORIES: [
      { query: CYBERSECURITY.MAIN, label: 'Cybersecurity - Top Story', type: null, url: null }
    ],
    LEADING_AND_SUPPORT: [
      {
        query: CYBERSECURITY.BREACHES,
        label: 'Data Breaches & Hacks',
        url: 'pro/cybersecurity/breaches-hacks',
        type: CYBERSECURITY.SECTION_ENUM.BREACHES
      },
      {
        query: CYBERSECURITY.CYBER_RISK,
        label: 'Regulation & Cyber Risk',
        url: 'pro/cybersecurity/regulation-risk',
        type: CYBERSECURITY.SECTION_ENUM.CYBER_RISK
      }
    ],
    MORE_LIST: [{ query: CYBERSECURITY.MAIN, label: 'More in Cybersecurity', type: null, url: null }]
  },
  politics: {
    SECTION_TOP: [{ query: POLITICS.MAIN, label: 'Politics', type: null, url: null }],
    SECTION_TOP_FIVE_STORIES: [{ query: POLITICS.MAIN, label: 'Politics', type: null, url: null }],
    LEADING_AND_SUPPORT: [
      {
        query: POLITICS.ELECTIONS,
        label: 'Elections',
        url: 'politics/elections',
        type: POLITICS.SECTION_ENUM.ELECTIONS
      },
      { query: POLITICS.POLICY, label: 'Policy', url: 'politics/policy', type: POLITICS.SECTION_ENUM.POLICY },
      {
        query: POLITICS.NATIONAL_SECURITY,
        label: 'National Security',
        url: 'politics/national-security',
        type: POLITICS.SECTION_ENUM.NATIONAL_SECURITY
      }
    ],
    MORE_LIST: [{ query: POLITICS.MAIN, label: 'More in Politics', type: null, url: null }],
    VIDEO_LIST: [{ query: POLITICS.VIDEO, label: 'Video', type: null, url: null }],
    RELATED_OPINION: [
      {
        query: POLITICS.RELATED_OPINION,
        label: 'Related opinion',
        url: '',
        type: POLITICS.SECTION_ENUM.RELATED_OPINION
      }
    ]
  },
  lifestyle: {
    SECTION_TOP: [{ query: LIFESTYLE.MAIN, label: 'Lifestyle', type: null, url: null }],
    SECTION_TOP_FIVE_STORIES: [{ query: LIFESTYLE.MAIN, label: 'Lifestyle', type: null, url: null }],
    LEADING_AND_SUPPORT: [
      {
        query: LIFESTYLE.TRAVEL,
        label: 'Travel',
        url: 'lifestyle/travel',
        type: LIFESTYLE.SECTION_ENUM.TRAVEL
      },
      { query: LIFESTYLE.CARS, label: 'Cars', url: 'lifestyle/cars', type: LIFESTYLE.SECTION_ENUM.CARS },
      {
        query: LIFESTYLE.CAREERS,
        label: 'Careers',
        url: 'lifestyle/careers',
        type: LIFESTYLE.SECTION_ENUM.CAREERS
      },
      {
        query: LIFESTYLE.WORKPLACE,
        label: 'Workplace',
        url: 'lifestyle/workplace',
        type: LIFESTYLE.SECTION_ENUM.WORKPLACE
      },
      { query: LIFESTYLE.FITNESS, label: 'Fitness', url: 'lifestyle/fitness', type: LIFESTYLE.SECTION_ENUM.FITNESS },
      {
        query: LIFESTYLE.RELATIONSHIPS,
        label: 'Relationships',
        url: 'lifestyle/relationships',
        type: LIFESTYLE.SECTION_ENUM.RELATIONSHIPS
      }
    ],
    MORE_LIST: [{ query: LIFESTYLE.MAIN, label: 'More in Lifestyle', type: null, url: null }],
    VIDEO_LIST: [{ query: LIFESTYLE.VIDEO, label: 'Video', type: null, url: null }],
    RELATED_OPINION: [
      {
        query: LIFESTYLE.RELATED_OPINION,
        label: 'Related opinion',
        url: '',
        type: LIFESTYLE.SECTION_ENUM.RELATED_OPINION
      }
    ]
  },
  'real-estate': {
    SECTION_TOP: [{ query: REAL_ESTATE.MAIN, label: 'Real Estate', type: null, url: null }],
    SECTION_TOP_FIVE_STORIES: [{ query: REAL_ESTATE.MAIN, label: 'Real Estate', type: null, url: null }],
    LEADING_AND_SUPPORT: [
      {
        query: REAL_ESTATE.LUXURY_HOMES,
        label: 'Luxury Homes',
        url: 'real-estate/luxury-homes',
        type: REAL_ESTATE.SECTION_ENUM.LUXURY_HOMES
      },
      {
        query: REAL_ESTATE.COMMERCIAL,
        label: 'Commercial',
        url: 'real-estate/commercial',
        type: REAL_ESTATE.SECTION_ENUM.COMMERCIAL
      }
    ],
    MORE_LIST: [{ query: REAL_ESTATE.MAIN, label: 'More in Real Estate', type: null, url: null }],
    VIDEO_LIST: [{ query: REAL_ESTATE.VIDEO, label: 'Video', type: null, url: null }],
    RELATED_OPINION: [
      {
        query: REAL_ESTATE.RELATED_OPINION,
        label: 'Related opinion',
        url: '',
        type: REAL_ESTATE.SECTION_ENUM.RELATED_OPINION
      }
    ]
  },
  finance: {
    SECTION_TOP: [{ query: FINANCE.MAIN, label: 'Markets & Finance', type: null, url: null }],
    SECTION_TOP_FIVE_STORIES: [{ query: FINANCE.MAIN, label: 'Markets & Finance', type: null, url: null }],
    LEADING_AND_SUPPORT: [
      {
        query: FINANCE.HEARD_ON_THE_STREET,
        label: 'Heard on the Street',
        url: 'finance/heard-on-the-street',
        type: FINANCE.SECTION_ENUM.HEARD_ON_THE_STREET
      },
      {
        query: FINANCE.BANKING,
        label: 'Banking',
        url: 'finance/banking',
        type: FINANCE.SECTION_ENUM.BANKING
      },
      {
        query: FINANCE.INVESTING,
        label: 'Investing',
        url: 'finance/investing',
        type: FINANCE.SECTION_ENUM.INVESTING
      },
      {
        query: FINANCE.COMMODITIES,
        label: 'Commodities & Futures',
        url: 'finance/commodities-futures',
        type: FINANCE.SECTION_ENUM.COMMODITIES
      },
      {
        query: FINANCE.CURRENCIES,
        label: 'Currencies',
        url: 'finance/currencies',
        type: FINANCE.SECTION_ENUM.CURRENCIES
      },
      {
        query: FINANCE.REGULATION,
        label: 'Regulation',
        url: 'finance/regulation',
        type: FINANCE.SECTION_ENUM.REGULATION
      },
      {
        query: FINANCE.STOCKS,
        label: 'Stocks',
        url: 'finance/stocks',
        type: FINANCE.SECTION_ENUM.STOCKS
      }
    ],
    MORE_LIST: [{ query: FINANCE.MAIN, label: 'More in Markets & Finance', type: null, url: null }],
    VIDEO_LIST: [{ query: FINANCE.VIDEO, label: 'Video', type: null, url: null }],
    RELATED_OPINION: [
      {
        query: FINANCE.RELATED_OPINION,
        label: 'Related opinion',
        url: '',
        type: FINANCE.SECTION_ENUM.RELATED_OPINION
      }
    ]
  },
  'us-news': {
    SECTION_TOP: [{ query: US_NEWS.MAIN, label: 'U.S. News', type: null, url: null }],
    SECTION_TOP_FIVE_STORIES: [{ query: US_NEWS.MAIN, label: 'U.S. News', type: null, url: null }],
    LEADING_AND_SUPPORT: [
      {
        query: US_NEWS.LAW,
        label: 'Law',
        url: 'us-news/law',
        type: US_NEWS.SECTION_ENUM.LAW
      },
      {
        query: US_NEWS.CLIMATE,
        label: 'Climate & Environment',
        url: 'us-news/climate-environment',
        type: US_NEWS.SECTION_ENUM.CLIMATE
      },
      {
        query: US_NEWS.EDUCATION,
        label: 'Education',
        url: 'us-news/education',
        type: US_NEWS.SECTION_ENUM.EDUCATION
      }
    ],
    MORE_LIST: [{ query: US_NEWS.MAIN, label: 'More in U.S.', type: null, url: null }],
    VIDEO_LIST: [{ query: US_NEWS.VIDEO, label: 'Video', type: null, url: null }],
    RELATED_OPINION: [
      {
        query: US_NEWS.RELATED_OPINION,
        label: 'Related opinion',
        url: '',
        type: US_NEWS.SECTION_ENUM.RELATED_OPINION
      }
    ]
  },
  business: {
    SECTION_TOP: [{ query: BUSINESS.MAIN, label: 'Business', type: null, url: null }],
    SECTION_TOP_FIVE_STORIES: [{ query: BUSINESS.MAIN, label: 'Business', type: null, url: null }],
    LEADING_AND_SUPPORT: [
      {
        query: BUSINESS.HOSPITALITY,
        label: 'Hospitality',
        url: 'business/hospitality',
        type: BUSINESS.SECTION_ENUM.HOSPITALITY
      },
      { query: BUSINESS.RETAIL, label: 'Retail', url: 'business/retail', type: BUSINESS.SECTION_ENUM.RETAIL },
      {
        query: BUSINESS.EARNINGS,
        label: 'Earnings',
        url: 'business/earnings',
        type: BUSINESS.SECTION_ENUM.EARNINGS
      },
      {
        query: BUSINESS.ENERGY,
        label: 'Energy & Oil',
        url: 'business/energy-oil',
        type: BUSINESS.SECTION_ENUM.ENERGY
      },
      {
        query: BUSINESS.AUTOS,
        label: 'Autos',
        url: 'business/autos',
        type: BUSINESS.SECTION_ENUM.AUTOS
      },
      {
        query: BUSINESS.MEDIA,
        label: 'Media',
        url: 'business/media',
        type: BUSINESS.SECTION_ENUM.MEDIA
      },
      {
        query: BUSINESS.LOGISTICS,
        label: 'Logistics',
        url: 'business/logistics',
        type: BUSINESS.SECTION_ENUM.LOGISTICS
      },
      {
        query: BUSINESS.DEALS,
        label: 'Deals',
        url: 'business/deals',
        type: BUSINESS.SECTION_ENUM.DEALS
      },
      {
        query: BUSINESS.TELECOM,
        label: 'Telecom',
        url: 'business/telecom',
        type: BUSINESS.SECTION_ENUM.TELECOM
      },
      {
        query: BUSINESS.AIRLINES,
        label: 'Airlines',
        url: 'business/airlines',
        type: BUSINESS.SECTION_ENUM.AIRLINES
      },
      {
        query: BUSINESS.ENTREPRENEURSHIP,
        label: 'Entrepreneurship',
        url: 'business/entrepreneurship',
        type: BUSINESS.SECTION_ENUM.ENTREPRENEURSHIP
      },
      {
        query: BUSINESS.C_SUITE,
        label: 'C-Suite',
        url: 'business/c-suite',
        type: BUSINESS.SECTION_ENUM.C_SUITE
      }
    ],
    MORE_LIST: [{ query: BUSINESS.MAIN, label: 'More in Business', type: null, url: null }],
    VIDEO_LIST: [{ query: BUSINESS.VIDEO, label: 'Video', type: null, url: null }],
    RELATED_OPINION: [
      {
        query: BUSINESS.RELATED_OPINION,
        label: 'Related opinion',
        url: '',
        type: BUSINESS.SECTION_ENUM.RELATED_OPINION
      }
    ]
  },
  economy: {
    SECTION_TOP: [{ query: ECONOMY.MAIN, label: 'Economy', type: null, url: null }],
    SECTION_TOP_FIVE_STORIES: [{ query: ECONOMY.MAIN, label: 'Economy', type: null, url: null }],
    LEADING_AND_SUPPORT: [
      {
        query: ECONOMY.GLOBAL,
        label: 'Global',
        url: 'economy/global',
        type: ECONOMY.SECTION_ENUM.GLOBAL
      },
      { query: ECONOMY.TRADE, label: 'Trade', url: 'economy/trade', type: ECONOMY.SECTION_ENUM.TRADE },
      {
        query: ECONOMY.CENTRAL_BANKING,
        label: 'Central Banking',
        url: 'economy/central-banking',
        type: ECONOMY.SECTION_ENUM.CENTRAL_BANKING
      },
      {
        query: ECONOMY.HOUSING,
        label: 'Housing',
        url: 'economy/housing',
        type: ECONOMY.SECTION_ENUM.HOUSING
      },
      {
        query: ECONOMY.JOBS,
        label: 'Jobs',
        url: 'economy/jobs',
        type: ECONOMY.SECTION_ENUM.JOBS
      },
      {
        query: ECONOMY.CONSUMERS,
        label: 'Consumers',
        url: 'economy/consumers',
        type: ECONOMY.SECTION_ENUM.CONSUMERS
      }
    ],
    MORE_LIST: [{ query: ECONOMY.MAIN, label: 'More in Economy', type: null, url: null }],
    VIDEO_LIST: [{ query: ECONOMY.VIDEO, label: 'Video', type: null, url: null }],
    RELATED_OPINION: [
      {
        query: ECONOMY.RELATED_OPINION,
        label: 'Related opinion',
        url: '',
        type: ECONOMY.SECTION_ENUM.RELATED_OPINION
      }
    ]
  },
  tech: {
    SECTION_TOP: [{ query: TECH.MAIN, label: 'Technology', type: null, url: null }],
    SECTION_TOP_FIVE_STORIES: [{ query: TECH.MAIN, label: 'Technology', type: null, url: null }],
    LEADING_AND_SUPPORT: [
      {
        query: TECH.AI,
        label: 'Artificial Intelligence',
        url: 'tech/ai',
        type: TECH.SECTION_ENUM.AI
      },
      {
        query: TECH.PERSONAL_TECHNOLOGY,
        label: 'Personal Technology',
        url: 'tech/personal-tech',
        type: TECH.SECTION_ENUM.PERSONAL_TECHNOLOGY
      },
      {
        query: TECH.BIOTECH,
        label: 'Biotech',
        url: 'tech/biotech',
        type: TECH.SECTION_ENUM.BIOTECH
      }
    ],
    MORE_LIST: [{ query: TECH.MAIN, label: 'More in Technology', type: null, url: null }],
    VIDEO_LIST: [{ query: TECH.VIDEO, label: 'Video', type: null, url: null }],
    RELATED_OPINION: [
      {
        query: TECH.RELATED_OPINION,
        label: 'Related opinion',
        url: '',
        type: TECH.SECTION_ENUM.RELATED_OPINION
      }
    ]
  },
  'personal-finance': {
    SECTION_TOP: [{ query: PERSONAL_FINANCE.MAIN, label: 'Personal Finance', type: null, url: null }],
    SECTION_TOP_FIVE_STORIES: [{ query: PERSONAL_FINANCE.MAIN, label: 'Personal Finance', type: null, url: null }],
    LEADING_AND_SUPPORT: [
      {
        query: PERSONAL_FINANCE.MORTGAGES,
        label: 'Mortgages',
        url: 'personal-finance/mortgages',
        type: PERSONAL_FINANCE.SECTION_ENUM.MORTGAGES
      },
      {
        query: PERSONAL_FINANCE.CREDIT,
        label: 'Credit',
        url: 'personal-finance/credit',
        type: PERSONAL_FINANCE.SECTION_ENUM.CREDIT
      },
      {
        query: PERSONAL_FINANCE.SAVINGS,
        label: 'Savings',
        url: 'personal-finance/savings',
        type: PERSONAL_FINANCE.SECTION_ENUM.SAVINGS
      },
      {
        query: PERSONAL_FINANCE.RETIREMENT,
        label: 'Retirement',
        url: 'personal-finance/retirement',
        type: PERSONAL_FINANCE.SECTION_ENUM.RETIREMENT
      },
      {
        query: PERSONAL_FINANCE.TAXES,
        label: 'Taxes',
        url: 'personal-finance/taxes',
        type: PERSONAL_FINANCE.SECTION_ENUM.TAXES
      }
    ],
    MORE_LIST: [{ query: PERSONAL_FINANCE.MAIN, label: 'More in Personal Finance', type: null, url: null }],
    VIDEO_LIST: [{ query: PERSONAL_FINANCE.VIDEO, label: 'Video', type: null, url: null }],
    RELATED_OPINION: [
      {
        query: PERSONAL_FINANCE.RELATED_OPINION,
        label: 'Related opinion',
        url: '',
        type: PERSONAL_FINANCE.SECTION_ENUM.RELATED_OPINION
      }
    ]
  },
  health: {
    SECTION_TOP: [{ query: HEALTH.MAIN, label: 'Health', type: null, url: null }],
    SECTION_TOP_FIVE_STORIES: [{ query: HEALTH.MAIN, label: 'Health', type: null, url: null }],
    LEADING_AND_SUPPORT: [
      {
        query: HEALTH.PHARMA,
        label: 'Pharma',
        url: 'health/pharma',
        type: HEALTH.SECTION_ENUM.PHARMA
      },
      { query: HEALTH.WELLNESS, label: 'Wellness', url: 'health/wellness', type: HEALTH.SECTION_ENUM.WELLNESS },
      {
        query: HEALTH.HEALTHCARE,
        label: 'Healthcare',
        url: 'health/healthcare',
        type: HEALTH.SECTION_ENUM.HEALTHCARE
      }
    ],
    MORE_LIST: [{ query: HEALTH.MAIN, label: 'More in Health', type: null, url: null }],
    VIDEO_LIST: [{ query: HEALTH.VIDEO, label: 'Video', type: null, url: null }],
    RELATED_OPINION: [
      {
        query: HEALTH.RELATED_OPINION,
        label: 'Related opinion',
        url: '',
        type: HEALTH.SECTION_ENUM.RELATED_OPINION
      }
    ]
  },
  style: {
    SECTION_TOP: [{ query: STYLE.MAIN, label: 'Style', type: null, url: null }],
    SECTION_TOP_FIVE_STORIES: [{ query: STYLE.MAIN, label: 'Style', type: null, url: null }],
    LEADING_AND_SUPPORT: [
      {
        query: STYLE.MAGAZINE,
        label: 'Magazine',
        url: null,
        type: STYLE.SECTION_ENUM.MAGAZINE
      },
      {
        query: STYLE.FASHION,
        label: 'Fashion',
        url: 'style/fashion',
        type: STYLE.SECTION_ENUM.FASHION
      },
      {
        query: STYLE.DESIGN,
        label: 'Design',
        url: 'style/design',
        type: STYLE.SECTION_ENUM.DESIGN
      },
      {
        query: STYLE.BEAUTY,
        label: 'Beauty',
        url: 'style/beauty',
        type: STYLE.SECTION_ENUM.BEAUTY
      }
    ],
    MORE_LIST: [{ query: STYLE.MAIN, label: 'More in Style', type: null, url: null }],
    VIDEO_LIST: [{ query: STYLE.VIDEO, label: 'Video', type: null, url: null }],
    RELATED_OPINION: [
      {
        query: STYLE.RELATED_OPINION,
        label: 'Related opinion',
        url: '',
        type: STYLE.SECTION_ENUM.RELATED_OPINION
      }
    ]
  },
  sports: {
    SECTION_TOP: [{ query: SPORTS.MAIN, label: 'Sports', type: null, url: null }],
    SECTION_TOP_FIVE_STORIES: [{ query: SPORTS.MAIN, label: 'Sports', type: null, url: null }],
    LEADING_AND_SUPPORT: [
      {
        query: SPORTS.FOOTBALL,
        label: 'Football',
        url: 'sports/football',
        type: SPORTS.SECTION_ENUM.FOOTBALL
      },
      { query: SPORTS.TENNIS, label: 'Tennis', url: 'sports/tennis', type: SPORTS.SECTION_ENUM.TENNIS },
      {
        query: SPORTS.BASEBALL,
        label: 'Baseball',
        url: 'sports/baseball',
        type: SPORTS.SECTION_ENUM.BASEBALL
      },
      {
        query: SPORTS.GOLF,
        label: 'Golf',
        url: 'sports/golf',
        type: SPORTS.SECTION_ENUM.GOLF
      },
      { query: SPORTS.SOCCER, label: 'Soccer', url: 'sports/soccer', type: SPORTS.SECTION_ENUM.SOCCER },
      {
        query: SPORTS.BASKETBALL,
        label: 'Basketball',
        url: 'lifessports/basketball',
        type: SPORTS.SECTION_ENUM.BASKETBALL
      },
      { query: SPORTS.OLYMPICS, label: 'Olympics', url: 'sports/olympics', type: SPORTS.SECTION_ENUM.OLYMPICS },
      { query: SPORTS.HOCKEY, label: 'Hockey', url: 'sports/hockey', type: SPORTS.SECTION_ENUM.HOCKEY }
    ],
    MORE_LIST: [{ query: SPORTS.MAIN, label: 'More in Sports', type: null, url: null }],
    VIDEO_LIST: [{ query: SPORTS.VIDEO, label: 'Video', type: null, url: null }],
    RELATED_OPINION: [
      {
        query: SPORTS.RELATED_OPINION,
        label: 'Related opinion',
        url: '',
        type: SPORTS.SECTION_ENUM.RELATED_OPINION
      }
    ]
  },
  science: {
    SECTION_TOP: [{ query: SCIENCE.MAIN, label: 'Science', type: null, url: null }],
    SECTION_TOP_FIVE_STORIES: [{ query: SCIENCE.MAIN, label: 'Science', type: null, url: null }],
    LEADING_AND_SUPPORT: [
      {
        query: SCIENCE.ENVIRONMENT,
        label: 'Environment',
        url: 'science/environment',
        type: SCIENCE.SECTION_ENUM.ENVIRONMENT
      },
      {
        query: SCIENCE.SPACE_ASTRONOMY,
        label: 'Space & Astronomy',
        url: 'science/space-astronomy',
        type: SCIENCE.SECTION_ENUM.SPACE_ASTRONOMY
      },
      {
        query: SCIENCE.ANTHROPOLOGY,
        label: 'Anthropology',
        url: 'science/archaeology',
        type: SCIENCE.SECTION_ENUM.ANTHROPOLOGY
      },
      {
        query: SCIENCE.BIOLOGY,
        label: 'Biology',
        url: 'science/biology',
        type: SCIENCE.SECTION_ENUM.BIOLOGY
      },
      {
        query: SCIENCE.PHYSICS,
        label: 'Physics',
        url: 'science/physics',
        type: SCIENCE.SECTION_ENUM.PHYSICS
      }
    ],
    MORE_LIST: [{ query: SCIENCE.MAIN, label: 'More in Science', type: null, url: null }],
    VIDEO_LIST: [{ query: SCIENCE.VIDEO, label: 'Video', type: null, url: null }],
    RELATED_OPINION: [
      {
        query: SCIENCE.RELATED_OPINION,
        label: 'Related opinion',
        url: '',
        type: SCIENCE.SECTION_ENUM.RELATED_OPINION
      }
    ]
  },
  'journal-reports': {
    SECTION_TOP: [{ query: JOURNAL_REPORTS.SECTION_TOP, label: 'Journal Reports', type: null, url: null }],
    SECTION_TOP_FIVE_STORIES: [{ query: JOURNAL_REPORTS.MAIN, label: 'Journal Reports', type: null, url: null }],
    MORE_LIST: [{ query: JOURNAL_REPORTS.MAIN, label: 'Latest', type: null, url: null }]
  },
  'future-of-everything': {
    SECTION_TOP: [
      { query: FUTURE_OF_EVERYTHING.SECTION_TOP, label: 'The Future of Everything', type: null, url: null }
    ],
    SECTION_TOP_FIVE_STORIES: [
      { query: FUTURE_OF_EVERYTHING.TOP_FIVE_STORIES, label: 'The Future of Everything', type: null, url: null }
    ],
    MORE_LIST: [
      { query: FUTURE_OF_EVERYTHING.MORE_LIST, label: 'More in The Future of Everything', type: null, url: null }
    ],
    RELATED_OPINION: [
      {
        query: FUTURE_OF_EVERYTHING.RELATED_OPINION,
        label: 'Related opinion',
        url: '',
        type: FUTURE_OF_EVERYTHING.SECTION_ENUM.RELATED_OPINION
      }
    ]
  },
  usa250: {
    SECTION_TOP: [{ query: USA250.MAIN, label: 'USA 250', type: null, url: null }],
    SECTION_TOP_FIVE_STORIES: [{ query: USA250.MAIN, label: 'USA 250', type: null, url: null }],
    LEADING_AND_SUPPORT: [{ query: USA250.LEADING_AND_SUPPORT, label: 'USA 250', type: null, url: null }],
    VIDEO_LIST: [{ query: USA250.VIDEO_LIST, label: 'Video', type: null, url: null }],
    MORE_LIST: [{ query: USA250.MAIN, label: 'More in USA 250', type: null, url: null }]
  },
  'pro_central-banking': {
    SECTION_TOP: [{ query: CENTRAL_BANKING.SECTION_TOP, label: 'Central Banking - Top Story', type: null, url: null }],
    SECTION_TOP_SIX_STORIES: [
      { query: CENTRAL_BANKING.MAIN, label: 'Central Banking - Lead Stories', type: null, url: null }
    ],
    LEADING_AND_SUPPORT: [
      {
        query: CENTRAL_BANKING.COMMENTARY_AND_ANALYSIS,
        label: 'Commentary & Analysis',
        url: 'pro/central-banking/commentary-analysis',
        type: CENTRAL_BANKING.SECTION_ENUM.COMMENTARY_AND_ANALYSIS
      },
      {
        query: CENTRAL_BANKING.ECONOMY,
        label: 'Economy',
        url: 'economy',
        type: CENTRAL_BANKING.SECTION_ENUM.ECONOMY
      },
      {
        query: CENTRAL_BANKING.RESEARCH,
        label: 'Research',
        url: 'pro/central-banking/research',
        type: CENTRAL_BANKING.SECTION_ENUM.RESEARCH
      },
      {
        query: CENTRAL_BANKING.FINANCIAL_REGULATION,
        label: 'Financial Regulation',
        url: 'pro/central-banking/financial-regulation',
        type: CENTRAL_BANKING.SECTION_ENUM.FINANCIAL_REGULATION
      }
    ],
    MORE_LIST: [{ query: CENTRAL_BANKING.MAIN, label: 'More in Central Banking', type: null, url: null }]
  },
  pro_bankruptcy: {
    SECTION_TOP: [{ query: BANKRUPTCY.SECTION_TOP, label: 'Bankruptcy - Top Story', type: null, url: null }],
    SECTION_TOP_SIX_STORIES: [{ query: BANKRUPTCY.MAIN, label: 'Bankruptcy - Top Stories', type: null, url: null }],
    LEADING_AND_SUPPORT: [
      {
        query: BANKRUPTCY.BANKRUPTCY,
        label: 'Bankruptcy',
        url: 'pro/bankruptcy/br-news',
        type: BANKRUPTCY.SECTION_ENUM.BANKRUPTCY
      },
      {
        query: BANKRUPTCY.DISTRESS,
        label: 'Distress',
        url: 'pro/bankruptcy/distress',
        type: BANKRUPTCY.SECTION_ENUM.DISTRESS
      },
      {
        query: BANKRUPTCY.COMMENTARY_ANALYSIS,
        label: 'Commentary & Analysis',
        url: 'pro/bankruptcy/commentary-analysis',
        type: BANKRUPTCY.SECTION_ENUM.COMMENTARY_AND_ANALYSIS
      }
    ],
    MORE_LIST: [{ query: BANKRUPTCY.MORE_LIST, label: 'More in Bankruptcy', type: null, url: null }]
  },
  pro_cybersecurity: {
    SECTION_TOP: [{ query: CYBERSECURITY.SECTION_TOP, label: 'Cybersecurity - Top Story', type: null, url: null }],
    SECTION_TOP_FIVE_STORIES: [
      { query: CYBERSECURITY.MAIN, label: 'Cybersecurity - Top Stories', type: null, url: null }
    ],
    LEADING_AND_SUPPORT: [
      {
        query: CYBERSECURITY.BREACHES,
        label: 'Data Breaches & Hacks',
        url: 'pro/cybersecurity/breaches-hacks',
        type: CYBERSECURITY.SECTION_ENUM.BREACHES
      },
      {
        query: CYBERSECURITY.CYBER_RISK,
        label: 'Regulation & Cyber Risk',
        url: 'pro/cybersecurity/regulation-risk',
        type: CYBERSECURITY.SECTION_ENUM.CYBER_RISK
      }
    ],
    MORE_LIST: [{ query: CYBERSECURITY.MAIN, label: 'More in Cybersecurity', type: null, url: null }]
  },
  'pro_venture-capital': {
    SECTION_TOP: [{ query: VENTURE_CAPITAL.SECTION_TOP, label: 'Venture Capital - Top Story', type: null, url: null }],
    SECTION_TOP_SIX_STORIES: [
      { query: VENTURE_CAPITAL.MAIN, label: 'Venture Capital - Lead Stories', type: null, url: null }
    ],
    LEADING_AND_SUPPORT: [
      {
        query: VENTURE_CAPITAL.NEW_MONEY,
        label: 'New Money',
        url: 'pro/venture-capital/new-money',
        type: VENTURE_CAPITAL.SECTION_ENUM.NEW_MONEY
      },
      {
        query: VENTURE_CAPITAL.COMMENTARY_AND_ANALYSIS,
        label: 'Commentary & Analysis',
        url: 'pro/venture-capital/commentary-analysis',
        type: VENTURE_CAPITAL.SECTION_ENUM.COMMENTARY_ANALYSIS
      },
      {
        query: VENTURE_CAPITAL.LIFE_SCIENCES,
        label: 'Life Sciences',
        url: 'pro/venture-capital/life-sciences',
        type: VENTURE_CAPITAL.SECTION_ENUM.LIFE_SCIENCES
      }
    ],
    MORE_LIST: [{ query: VENTURE_CAPITAL.MORE, label: 'More in Venture Capital', type: null, url: null }]
  },
  'logistics-report': {
    SECTION_TOP: [
      { query: LOGISTICS_REPORT.SECTION_TOP, label: 'Logistics Report - Top Story', type: null, url: null }
    ],
    SECTION_TOP_FIVE_STORIES: [
      { query: LOGISTICS_REPORT.MAIN, label: 'Logistics Report - Lead Stories', type: null, url: null }
    ],
    LEADING_AND_SUPPORT: [
      {
        query: LOGISTICS_REPORT.RETAIL,
        label: 'Retail',
        url: 'business/retail',
        type: LOGISTICS_REPORT.SECTION_ENUM.RETAIL
      },
      {
        query: LOGISTICS_REPORT.SUSTAINABLE_BUSINESS,
        label: 'Sustainable Business',
        url: null,
        type: LOGISTICS_REPORT.SECTION_ENUM.SUSTAINABLE_BUSINESS
      },
      {
        query: LOGISTICS_REPORT.ECONOMY,
        label: 'Economy',
        url: 'economy',
        type: LOGISTICS_REPORT.SECTION_ENUM.ECONOMY
      }
    ],
    MORE_LIST: [{ query: LOGISTICS_REPORT.MORE_LIST, label: 'More in Logistics Report', type: null, url: null }]
  },
  'cio-journal': {
    SECTION_TOP_TWO_STORIES: [{ query: CIO_JOURNAL.SECTION_TOP, label: 'CIO - Lead Story', type: null, url: null }],
    SECTION_TOP_FOUR_STORIES: [{ query: CIO_JOURNAL.MAIN, label: 'CIO - Top Stories', type: null, url: null }],
    LEADING_AND_SUPPORT: [
      {
        query: CIO_JOURNAL.CYBERSECURITY,
        label: 'Cybersecurity',
        url: '/pro/cybersecurity',
        type: CIO_JOURNAL.SECTION_ENUM.CYBERSECURITY
      }
    ],
    VIDEO_LIST: [
      { query: CIO_JOURNAL.VIDEO, label: 'WSJ CIO Network', type: null, url: 'video/subject/wsj-cio-summit-2025' }
    ],
    MORE_LIST: [{ query: CIO_JOURNAL.MORE_LIST, label: 'More in CIO Journal', type: null, url: null }]
  },
  'cmo-today': {
    SECTION_TOP_TWO_STORIES: [{ query: CMO_TODAY.SECTION_TOP, label: 'CMO - Lead Story', type: null, url: null }],
    SECTION_TOP_FOUR_STORIES: [{ query: CMO_TODAY.MAIN, label: 'CMO - Top Stories', type: null, url: null }],
    LEADING_AND_SUPPORT: [
      {
        query: CMO_TODAY.TRACKING_CONSUMERS,
        label: 'Tracking Consumers',
        url: 'economy/consumers',
        type: CMO_TODAY.SECTION_ENUM.TRACKING_CONSUMERS
      }
    ],
    VIDEO_LIST: [
      { query: CMO_TODAY.VIDEO, label: 'WSJ CMO Network', type: null, url: 'video/subject/wsj-cmo-network-summit-2024' }
    ],
    MORE_LIST: [{ query: CMO_TODAY.MORE_LIST, label: 'More in CMO Today', type: null, url: null }]
  },
  'cfo-journal': {
    SECTION_TOP_TWO_STORIES: [{ query: CFO_JOURNAL.SECTION_TOP, label: 'CFO - Lead Story', type: null, url: null }],
    SECTION_TOP_FOUR_STORIES: [{ query: CFO_JOURNAL.MAIN, label: 'CFO - Top Stories', type: null, url: null }],
    LEADING_AND_SUPPORT: [
      {
        query: CFO_JOURNAL.ACCOUNTING_AND_TAXES,
        label: 'Accounting & Taxes',
        url: 'cfo-journal/accounting-taxes',
        type: CFO_JOURNAL.SECTION_ENUM.ACCOUNTING_AND_TAXES
      }
    ],
    VIDEO_LIST: [
      { query: CFO_JOURNAL.VIDEO, label: 'WSJ CFO Network', type: null, url: 'video/subject/wsj-cfo-summit-2025' }
    ],
    MORE_LIST: [{ query: CFO_JOURNAL.MORE_LIST, label: 'More in CFO Journal', type: null, url: null }]
  },
  'risk-compliance-journal': {
    SECTION_TOP_TWO_STORIES: [
      { query: RCJ.SECTION_TOP, label: 'Risk & Compliance - Top Story', type: null, url: null }
    ],
    SECTION_TOP_FOUR_STORIES: [{ query: RCJ.MAIN, label: 'Risk & Compliance - Lead Stories', type: null, url: null }],
    PODCAST_SECTION: [{ query: RCJ.AUDIO_PODCAST, label: 'Dow Jones Risk Journal Audio', type: null, url: null }],
    MORE_LIST: [{ query: RCJ.MORE_LIST, label: 'More in Risk & Compliance Journal', type: null, url: null }]
  },
  'pro_sustainable-business': {
    SECTION_TOP_TWO_STORIES: [
      { query: PRO_SB.SECTION_TOP, label: 'Sustainable Business - Top Story', type: null, url: null }
    ],
    SECTION_TOP_FOUR_STORIES: [
      { query: PRO_SB.MAIN, label: 'Sustainable Business - Lead Stories', type: null, url: null }
    ],
    MORE_LIST: [{ query: PRO_SB.MORE_LIST, label: 'More in Sustainable Business', type: null, url: null }]
  },
  'media-center-watch': {
    MOST_POPULAR_MEDIA_CENTER: [{ query: WATCH.LATEST_VIDEOS, label: 'Latest Videos', type: null, url: null }],
    HORIZONTAL_VERTICAL_VIDEOS: [{ query: WATCH.WATCH_NOW, label: '', type: null, url: null }],
    OPINION_HORIZONTAL_CARDS_FOURTEEN: [{ query: WATCH.OPINION, label: 'Opinion', type: null, url: null }],
    HORIZONTAL_PODCAST_S: [
      { query: WATCH.VIDEO_PODCASTS, label: 'Video Podcasts', type: null, url: null },
      // { query: WATCH.VIDEO_SERIES, label: 'Video Series', type: null, url: null },
      {
        query: WATCH.VIDEO_SERIES_JOANNA_STERN,
        label: 'Video Series: Tech Things with Joanna Stern',
        type: null,
        url: null
      },
      { query: WATCH.VIDEO_SERIES_THE_ONE, label: 'Video Series: The One', type: null, url: null },
      { query: WATCH.VIDEO_SERIES_EQUIPPED, label: 'Video Series: Equipped', type: null, url: null },
      { query: WATCH.VIDEO_SERIES_TECH_BEHIND, label: 'Video Series: Tech Behind', type: null, url: null },
      { query: WATCH.VIDEO_SERIES_THE_ECONOMICS_OF, label: 'Video Series: The Economics Of', type: null, url: null },
      { query: WATCH.VIDEO_SERIES_COVETED, label: 'Video Series: Coveted', type: null, url: null },
      { query: WATCH.VIDEO_SERIES_PRO_PERFECTED, label: 'Video Series: Pro Perfected', type: null, url: null }
    ]
  },
  'media-center-listen': {
    PODCAST: [{ query: LISTEN.LATEST_EPISODES, label: 'Latest Episodes', type: null, url: null }],
    PODCASTS_SMALL: [{ query: LISTEN.LATEST_NEWS, label: 'Listen to WSJ Stories', type: null, url: null }],
    OPINION_HORIZONTAL_CARDS_EIGHTEEN: [
      { query: LISTEN.LISTEN_TO_OPINION, label: 'Listen to Opinion', type: null, url: null }
    ],
    HORIZONTAL_AUDIO_PODCAST_S: [{ query: LISTEN.ALL_PODCASTS, label: 'All Podcasts', type: null, url: null }]
  },
  'pro_private-equity': {
    SECTION_TOP: [
      { query: PRO_PRIVATE_EQUITY.SECTION_TOP, label: 'Private Equity - Top Story', type: null, url: null }
    ],
    SECTION_TOP_SIX_STORIES: [
      { query: PRO_PRIVATE_EQUITY.SECTION_TOP_SIX, label: 'Private Equity - Lead Stories', type: null, url: null }
    ],
    LEADING_AND_SUPPORT: [
      {
        query: PRO_PRIVATE_EQUITY.COMMENTARY_AND_ANALYSIS,
        label: 'Commentary & Analysis',
        url: 'pro/private-equity/commentary-analysis',
        type: PRO_PRIVATE_EQUITY.SECTION_ENUM.COMMENTARY_AND_ANALYSIS
      },
      {
        query: PRO_PRIVATE_EQUITY.DEALS_AND_EXISTS,
        label: 'Deals & Exits',
        url: 'pro/private-equity/deals',
        type: PRO_PRIVATE_EQUITY.SECTION_ENUM.DEALS_AND_EXISTS
      },
      {
        query: PRO_PRIVATE_EQUITY.INDUSTRY_NEWS,
        label: 'Industry News & Limited Partners',
        url: 'pro/private-equity/industry-partners',
        type: PRO_PRIVATE_EQUITY.SECTION_ENUM.INDUSTRY_NEWS
      },
      {
        query: PRO_PRIVATE_EQUITY.FIRMS_AND_FUNDS,
        label: 'Firms & Funds',
        url: 'pro/private-equity/firms-funds',
        type: PRO_PRIVATE_EQUITY.SECTION_ENUM.FIRMS_AND_FUNDS
      }
    ],
    MORE_LIST: [{ query: PRO_PRIVATE_EQUITY.MORE_LIST, label: 'More in Private Equity', type: null, url: null }]
  },
  latest: {
    SECTION_LIST: [{ query: LATEST.MAIN, label: 'Latest', type: null, url: null }]
  },
  homepage: {
    DISCOVER_VIDEO_CAROUSEL: [
      {
        query: {
          query: {
            and: [
              { terms: { key: 'LanguageCode', value: ['en'] } },
              { terms: { key: 'Product', value: ['wsj opinion video', 'wsj opinion video podcast', 'wsj video'] } },
              { terms: { key: 'VideoType', value: ['vertical'] } }
            ],
            not: []
          },
          sort: [{ key: AllessehQuerySortKeyTypes.LiveDate, order: AllessehQuerySortOrderType.DESC }],
          count: 6
        },
        label: 'Discover videos',
        type: null,
        url: null
      }
    ]
  },
  default: {
    default: [
      { query: { ...DEFAULT_QUERY, query: COMMON_QUERY_RULES_CONFIG }, label: 'Custom Query', type: null, url: null }
    ]
  }
};

const proIds = ['pro_sustainable-business', 'pro_cybersecurity', 'pro_private-equity'];
const batch2 = [
  'journal-reports',
  'logistics-report',
  'future-of-everything',
  'risk-compliance-journal',
  'cfo-journal',
  'cio-journal',
  'cmo-today'
];

// This logic is temporary and will be simplified once the configuration is standardized,
// with each section defining its own base query

export const getWSJDefaultQuery = (allessehId: string, treatmentType: string): QueryOptionWsj => {
  if ((treatmentType as UiTitleTreatmentType) === UiTitleTreatmentType.LeadingAndSupport) {
    if (proIds.some((id) => allessehId.toLowerCase().includes(id.toLowerCase()))) {
      return { query: BASE_QUERY_PRO, label: 'Custom Query', type: null, url: null };
    }

    if (batch2.some((id) => allessehId.toLowerCase().includes(id.toLowerCase()))) {
      return { query: BASE_QUERY, label: 'Custom Query', type: null, url: null };
    }

    return {
      query: { ...DEFAULT_QUERY, query: COMMON_QUERY_RULES_CONFIG },
      label: 'Custom Query',
      type: null,
      url: null
    };
  }

  return QUERY_CONFIG_WSJ.default.default[0];
};
